import React, { forwardRef } from "react";

/**
 * Enhanced toggle switch component with comprehensive styling, accessibility, and UX features.
 *
 * Features:
 * - Clear, accessible labels
 * - Optional description
 * - Focus and hover states
 * - Dark mode support
 * - Customizable size and color
 *
 * @param {Object} props
 * @param {string} props.id - Toggle switch ID (generated if not provided)
 * @param {string} props.name - Toggle switch name attribute
 * @param {string} props.label - Label text
 * @param {boolean} props.checked - Whether the toggle is checked
 * @param {Function} props.onChange - Change handler
 * @param {string} props.description - Optional description text
 * @param {boolean} props.disabled - Whether the toggle is disabled
 * @param {string} props.size - Toggle size (sm, md, lg)
 * @param {string} props.variant - Color variant (primary, success, danger, etc.)
 * @param {string} props.className - Additional class names
 */
const ToggleSwitch = forwardRef(
  (
    {
      id,
      name,
      label,
      checked = false,
      onChange,
      description,
      disabled = false,
      size = "md",
      variant = "primary",
      className = "",
      ...props
    },
    ref,
  ) => {
    // Generate unique ID if not provided
    const toggleId =
      id || `toggle-${name || Math.random().toString(36).substr(2, 9)}`;

    // Size classes for the toggle
    const sizeClasses = {
      sm: {
        toggle: "h-4 w-7",
        circle: "h-3 w-3",
        translate: "translate-x-3",
        label: "text-xs",
        description: "text-xs",
      },
      md: {
        toggle: "h-5 w-9",
        circle: "h-4 w-4",
        translate: "translate-x-4",
        label: "text-sm",
        description: "text-xs",
      },
      lg: {
        toggle: "h-6 w-11",
        circle: "h-5 w-5",
        translate: "translate-x-5",
        label: "text-base",
        description: "text-sm",
      },
    };

    // Variant classes for the toggle
    // Signal: every variant is monochrome (accent when on, raised when off);
    // colour stays reserved for live state. Variant names are kept for API
    // compatibility.
    const signalTrack = {
      active: "bg-sg-accent border-transparent",
      inactive: "bg-sg-raised-2 border-sg-line-strong",
    };
    const variantClasses = {
      primary: signalTrack,
      success: signalTrack,
      danger: signalTrack,
      warning: signalTrack,
      info: signalTrack,
    };

    // Handle toggle change
    const handleChange = (e) => {
      if (!disabled && onChange) {
        onChange(e);
      }
    };

    return (
      <div className={`flex items-start ${className}`}>
        <div className="flex items-center h-5">
          {/* Hidden checkbox for accessibility */}
          <input
            ref={ref}
            id={toggleId}
            name={name}
            type="checkbox"
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            className="sr-only"
            aria-checked={checked}
            aria-disabled={disabled}
            {...props}
          />

          {/* Custom toggle switch */}
          <label
            htmlFor={toggleId}
            className={`
            relative inline-flex items-center flex-shrink-0 cursor-pointer rounded-full border px-px transition-colors duration-150 ease-out
            ${sizeClasses[size].toggle}
            ${checked ? variantClasses[variant].active : variantClasses[variant].inactive}
            ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[color:var(--sg-focus)]
          `}
          >
            <span className="sr-only">{label}</span>
            <span
              aria-hidden="true"
              className={`
              pointer-events-none inline-block rounded-full transform transition duration-150 ease-out ${checked ? "bg-sg-accent-ink" : "bg-sg-ink-3"}
              ${sizeClasses[size].circle}
              ${checked ? sizeClasses[size].translate : "translate-x-0"}
            `}
            />
          </label>
        </div>

        {/* Label and description */}
        {(label || description) && (
          <div className="ml-3">
            {label && (
              <label
                htmlFor={toggleId}
                className={`
                font-medium text-sg-ink
                ${sizeClasses[size].label}
                ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              `}
              >
                {label}
              </label>
            )}
            {description && (
              <p
                className={`
              text-sg-ink-2 mt-0.5
              ${sizeClasses[size].description}
              ${disabled ? "opacity-50" : ""}
            `}
              >
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

ToggleSwitch.displayName = "ToggleSwitch";

export default ToggleSwitch;
